import { NIVEIS } from './niveis.js';
/**
 * O ALCANCE de um nível — e a ordem entre dois deles.
 *
 * Existe para responder à pergunta que o documento 19 faz a cada
 * mudança na matriz: "isto ELEVA o acesso?". Com célula booleana a
 * resposta seria trivial. Com célula de NÍVEL ela exige definição,
 * porque os níveis não formam uma escada: "Alunos das próprias turmas"
 * e "Os próprios dados" não são um maior que o outro — cada um alcança
 * linhas que o outro não alcança.
 *
 * A definição, dimensão por dimensão:
 *
 *   ação       conjunto de ações; contido é subconjunto
 *   escopo     `nenhum` ⊂ qualquer escopo ⊂ `todos`; dois escopos
 *              "próprios" diferentes são INCOMPARÁVEIS
 *   projeção   `basico` ⊂ qualquer projeção ⊂ `completo`; as variantes
 *              "sem X" são incomparáveis entre si
 *
 * Um nível B está CONTIDO num nível A quando está contido nas três
 * dimensões ao mesmo tempo. ELEVAR é trocar A por um B que NÃO está
 * contido em A — ganhar alguma coisa em qualquer das três, mesmo que
 * perca outra. Trocar "Alunos das próprias turmas" por "Os próprios
 * dados" eleva: ganha `editar` e um escopo que não tinha, ainda que
 * perca o das turmas.
 *
 * Incomparável conta como elevação de propósito: é o lado seguro do
 * erro. Uma regra que precisasse PROVAR que o escopo novo é maior
 * deixaria passar tudo o que ela não soubesse comparar.
 *
 * Mora em @arena/shared porque os dois lados perguntam a mesma coisa: o
 * servidor para recusar, a tela de #v-acessos para nem oferecer a opção.
 *
 * Ver: Arena_backend/Docs/plano/19-permissoes-auditoria.md
 */
/** O que uma célula ausente ou ilegível concede: nada. */
export const NEGADO = Object.freeze({
    rotulo: 'Não',
    acoes: [],
    escopo: 'nenhum',
    projecao: 'basico',
});
/**
 * O nível `chave` do recurso, se ele existir.
 *
 * `hasOwnProperty`, e não `NIVEIS[recurso][chave]` direto: a chave pode
 * vir de uma URL, e `NIVEIS.agenda['constructor']` devolve a função
 * `Object` — um valor "verdadeiro" que passaria por nível válido.
 */
export function nivelDoRecurso(recurso, chave) {
    const niveis = NIVEIS[recurso];
    return Object.prototype.hasOwnProperty.call(niveis, chave) ? niveis[chave] : undefined;
}
/**
 * O nível de uma célula para fins de COMPARAÇÃO. Ausente ou desconhecido
 * é `NEGADO` — o mesmo que o motor faz ao montar o Principal, onde a
 * linha que não traduz é negada.
 */
export function nivelOuNegado(recurso, chave) {
    return (chave ? nivelDoRecurso(recurso, chave) : undefined) ?? NEGADO;
}
export function escopoContido(dentro, fora) {
    return dentro === fora || dentro === 'nenhum' || fora === 'todos';
}
export function projecaoContida(dentro, fora) {
    return dentro === fora || dentro === 'basico' || fora === 'completo';
}
/**
 * `dentro` não alcança nada que `fora` não alcance?
 *
 * Nível sem ação nenhuma está contido em qualquer outro: escopo e
 * projeção só dizem SOBRE O QUE as ações valem, e sem ação não há o que
 * valer.
 */
export function nivelContido(dentro, fora) {
    if (dentro.acoes.length === 0)
        return true;
    return (dentro.acoes.every((acao) => fora.acoes.includes(acao)) &&
        escopoContido(dentro.escopo, fora.escopo) &&
        projecaoContida(dentro.projecao, fora.projecao));
}
/**
 * O que se GANHA indo de `de` para `para` — ou `null`, quando `para`
 * está contido em `de` (a troca não eleva nada).
 *
 * É a mesma pergunta de `nivelContido`, respondida com o motivo: a
 * trilha de auditoria de uma tentativa recusada precisa dizer O QUE a
 * pessoa tentou ganhar, não só que tentou.
 */
export function ganho(de, para) {
    if (nivelContido(para, de))
        return null;
    // Quem não tinha ação nenhuma ganha o nível inteiro — inclusive o
    // escopo e a projeção, que antes não valiam para nada.
    const semAcao = de.acoes.length === 0;
    const escopoGanho = semAcao || !escopoContido(para.escopo, de.escopo);
    const projecaoGanha = semAcao || !projecaoContida(para.projecao, de.projecao);
    return {
        acoes: para.acoes.filter((acao) => !de.acoes.includes(acao)),
        escopo: escopoGanho && para.escopo !== de.escopo ? { de: de.escopo, para: para.escopo } : null,
        projecao: projecaoGanha && para.projecao !== de.projecao
            ? { de: de.projecao, para: para.projecao }
            : null,
    };
}
/**
 * O que um perfil ganharia trocando as células `de` pelas `para`,
 * recurso por recurso. Objeto vazio: `para` cabe inteiro em `de`.
 *
 * É a comparação de quem MUDA DE PERFIL — o caso da pessoa que se move
 * para um perfil mais amplo, que também é elevar o próprio acesso.
 */
export function ganhosDoPerfil(recursos, de, para) {
    const ganhos = {};
    for (const recurso of recursos) {
        const g = ganho(nivelOuNegado(recurso, de[recurso]), nivelOuNegado(recurso, para[recurso]));
        if (g)
            ganhos[recurso] = g;
    }
    return ganhos;
}
/**
 * As chaves de nível do recurso que concedem a ação.
 *
 * Derivado do catálogo, não escrito à mão: é o que a trava do "último
 * editor" consulta para saber quem ainda edita permissões. Se um nível
 * novo de `perfis` com `editar` aparecer amanhã, a trava o enxerga sem
 * mudar uma linha.
 */
export function niveisQueConcedem(recurso, acao) {
    return Object.entries(NIVEIS[recurso])
        .filter(([, nivel]) => nivel.acoes.includes(acao))
        .map(([chave]) => chave);
}
//# sourceMappingURL=alcance.js.map